import { useQuery } from "@tanstack/react-query";

import { useTenant } from "@/contexts/tenant-provider";
import { useUser } from "@/contexts/user-provider";

import { useModule } from "@/hooks/use-module";

import { formatDate, formatQuantity, formatTime } from "@/lib/format-data";
import { PERMISSIONS, hasAnyPermission } from "@/lib/permissions";

import type { CoupaReconciliationRow, CoupaSendMode } from "@/types/coupa";

import { coupaQueryOptions } from "@/query/integrations/coupa";

export const COUPA_SETTINGS_ROUTE = "/settings/coupa";
/** The Decoda vs Coupa tab keeps the old Reconciliation tab's key, so earlier links and alerts still land on it. */
export const COUPA_REPORT_TAB = "reconciliation";
export const COUPA_REPORT_ROUTE = `${COUPA_SETTINGS_ROUTE}?tab=${COUPA_REPORT_TAB}`;

/** The same gate as the report's API: anyone who works with stock, or can see the Coupa settings. */
export const COUPA_REPORT_PERMISSIONS = [
  PERMISSIONS.INVENTORY_READ,
  PERMISSIONS.SETTINGS_COUPA_READ,
];

/** Whether this clinic compares with Coupa and the user may see the comparison. HQ holds no stock. */
export function useCanReadCoupaReport(): boolean {
  const hasCoupa = useModule("COUPA");
  const { tenant } = useTenant();
  const { currentUser } = useUser();
  return (
    hasCoupa &&
    tenant?.kind !== "MASTER" &&
    hasAnyPermission(currentUser, COUPA_REPORT_PERMISSIONS)
  );
}

export function useCoupaReport() {
  const enabled = useCanReadCoupaReport();
  return useQuery({ ...coupaQueryOptions.reconciliation(), enabled });
}

export const rowKey = (row: CoupaReconciliationRow) =>
  `${row.locationId}:${row.stockId}`;

export const isDifferent = (row: CoupaReconciliationRow) =>
  row.status === "DIFFERS" || row.status === "REFUSED_BY_COUPA";

/** What "Only differences" keeps: a location that differs, or one Coupa's count couldn't be read for, since neither can be called a match. */
export const isUnmatched = (row: CoupaReconciliationRow) =>
  isDifferent(row) || row.status === "CANT_COMPARE";

export const reportLink = (stockId?: string) =>
  stockId
    ? `${COUPA_REPORT_ROUTE}&stockId=${encodeURIComponent(stockId)}`
    : COUPA_REPORT_ROUTE;

const amount = (quantity: number, row: CoupaReconciliationRow) =>
  `${formatQuantity(Math.abs(quantity))} ${row.uomCode}`;

export const when = (moment: string, timeZone?: string) =>
  `${formatDate(moment, { weekday: "short", month: "short", day: "numeric", timeZone })} at ${formatTime(moment, true, timeZone)}`;

export const plural = (count: number, one: string, many: string) =>
  `${count} ${count === 1 ? one : many}`;

/** "Lewes", "Lewes and Middletown", "Dover, Lewes and Middletown" */
export function listed(names: string[]): string {
  if (names.length <= 1) return names.join("");
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

export const checkedAtText = (checkedAt: string) =>
  `${formatDate(checkedAt, { month: "short", day: "numeric" })}, ${formatTime(checkedAt)}`;

/** The Check column. */
export function checkLabel(row: CoupaReconciliationRow): string {
  switch (row.status) {
    case "MATCHES":
      return "Matches";
    case "MATCHES_ONCE_SENT":
      return "Matches once sent";
    case "WAITS_FOR_WHOLE_UNIT":
      return "Matches once a whole unit is sent";
    case "REFUSED_BY_COUPA":
      return "Coupa refused a change";
    case "DIFFERS":
      return `Off by ${formatQuantity(Math.abs(row.driftQuantity ?? 0))}`;
    case "CANT_COMPARE":
      return "No Coupa count";
  }
}

/** What an opened row says about its two counts. */
export function statusSentence(
  row: CoupaReconciliationRow,
  timeZone?: string,
): string {
  const decoda = amount(row.decodaQuantity, row);
  const coupa = amount(row.coupaQuantity ?? 0, row);
  const waiting = amount(row.inFlightQuantity, row);
  switch (row.status) {
    case "MATCHES":
      return `Decoda and Coupa both show ${decoda}.`;
    case "MATCHES_ONCE_SENT":
      return `Decoda shows ${decoda} and Coupa ${coupa}. The ${waiting} waiting to send makes up the difference, so Coupa will show ${decoda} too once it's sent.`;
    case "WAITS_FOR_WHOLE_UNIT":
      return `Decoda shows ${decoda} and Coupa ${coupa}. The ${waiting} waiting is less than one ${row.uomCode}, and Coupa only takes whole units for this item, so it's sent once the waiting changes add up to one.`;
    case "REFUSED_BY_COUPA":
      return `Decoda shows ${decoda} and Coupa ${coupa}. Coupa refused a change Decoda sent, so the two match only once it's retried from Needs you on the Sync tab.`;
    case "CANT_COMPARE":
      return `Coupa's count for this item at ${row.locationName} couldn't be read, so the two can't be compared.`;
    case "DIFFERS": {
      const drift = row.driftQuantity ?? 0;
      const gap = amount(drift, row);
      const counts =
        row.inFlightQuantity === 0
          ? `Decoda shows ${decoda} and Coupa ${coupa}, so ${drift > 0 ? "Decoda" : "Coupa"} has ${gap} more. Nothing waiting to send would close the gap.`
          : `Decoda shows ${decoda} and Coupa ${coupa}. Once the ${waiting} waiting is sent, Coupa would show ${amount((row.coupaQuantity ?? 0) + row.inFlightQuantity, row)}, still ${gap} ${drift > 0 ? "less" : "more"} than Decoda.`;
      return row.lastMatchedAt
        ? `${counts} They last matched ${when(row.lastMatchedAt, timeZone)}.`
        : counts;
    }
  }
}

/** What the description adds when Decoda isn't sending on its own. */
export function sendingSentence(sendMode: CoupaSendMode | null): string {
  if (sendMode === "HELD")
    return " Sending to Coupa is paused, so waiting changes stay waiting.";
  if (sendMode === "MANUAL")
    return " Waiting changes go to Coupa when someone runs Sync now.";
  return "";
}

/** One stock item and its comparison at each location. */
export interface ReportItem {
  stockId: string;
  stockName: string;
  uomCode: string;
  rows: CoupaReconciliationRow[];
}

export function groupByItem(rows: CoupaReconciliationRow[]): ReportItem[] {
  const items = new Map<string, ReportItem>();
  for (const row of rows) {
    const item = items.get(row.stockId) ?? {
      stockId: row.stockId,
      stockName: row.stockName,
      uomCode: row.uomCode,
      rows: [],
    };
    item.rows.push(row);
    items.set(row.stockId, item);
  }
  return Array.from(items.values()).sort((a, b) =>
    a.stockName.localeCompare(b.stockName),
  );
}

const STATUS_RANK: Record<CoupaReconciliationRow["status"], number> = {
  MATCHES: 0,
  WAITS_FOR_WHOLE_UNIT: 1,
  MATCHES_ONCE_SENT: 2,
  CANT_COMPARE: 3,
  REFUSED_BY_COUPA: 4,
  DIFFERS: 5,
};

/** An item stands as its worst location does. */
export function itemStatus(item: ReportItem): CoupaReconciliationRow["status"] {
  return item.rows.reduce<CoupaReconciliationRow["status"]>(
    (worst, row) =>
      STATUS_RANK[row.status] > STATUS_RANK[worst] ? row.status : worst,
    "MATCHES",
  );
}

/** The item row's Check: where it's off, or how it stands everywhere. */
export function itemCheckLabel(item: ReportItem): string {
  const status = itemStatus(item);
  const at = item.rows.filter((row) => row.status === status);
  if (status === "DIFFERS") {
    return at.length === 1
      ? `${checkLabel(at[0])} at ${at[0].locationName}`
      : `Off at ${at.length} locations`;
  }
  if (status === "REFUSED_BY_COUPA") {
    return at.length === 1
      ? `Coupa refused a change at ${at[0].locationName}`
      : `Coupa refused changes at ${at.length} locations`;
  }
  if (status === "CANT_COMPARE") {
    return at.length === 1
      ? `No Coupa count at ${at[0].locationName}`
      : `No Coupa count at ${at.length} locations`;
  }
  return checkLabel(at[0]);
}

export function itemTotals(item: ReportItem) {
  const counted = item.rows.filter((row) => row.coupaQuantity !== null);
  return {
    decoda: item.rows.reduce((sum, row) => sum + row.decodaQuantity, 0),
    coupa:
      counted.length === 0
        ? null
        : counted.reduce((sum, row) => sum + (row.coupaQuantity ?? 0), 0),
    waiting: item.rows.reduce((sum, row) => sum + row.inFlightQuantity, 0),
  };
}

/** The tiles, counted by item so they agree with the rows. */
export function itemCounts(items: ReportItem[]) {
  const statuses = items.map(itemStatus);
  const count = (...wanted: CoupaReconciliationRow["status"][]) =>
    statuses.filter((status) => wanted.includes(status)).length;
  return {
    items: items.length,
    matches: count("MATCHES"),
    matchesOnceSent: count("MATCHES_ONCE_SENT", "WAITS_FOR_WHOLE_UNIT"),
    differs: count("DIFFERS", "REFUSED_BY_COUPA"),
    cantCompare: count("CANT_COMPARE"),
  };
}

/** Distinct stock items that differ from Coupa at any location. */
export const differingItemCount = (rows: CoupaReconciliationRow[]) =>
  new Set(rows.filter(isDifferent).map((row) => row.stockId)).size;

/** Distinct stock items with changes still waiting to send at any location. */
export const waitingItemCount = (rows: CoupaReconciliationRow[]) =>
  new Set(
    rows.filter((row) => row.inFlightQuantity !== 0).map((row) => row.stockId),
  ).size;
